import React, { useState, useEffect } from 'react';
import { 
  X, 
  CheckCircle2, 
  CheckSquare,
  Circle, 
  FlaskConical, 
  Shield, 
  User, 
  GraduationCap, 
  BookOpen, 
  Video, 
  Calendar, 
  FileText, 
  Layers, 
  Activity, 
  RotateCcw, 
  Copy, 
  Check, 
  Smartphone, 
  ExternalLink,
  Lock,
  Sparkles,
  Zap,
  Info
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useRouter } from '../../router/RouterContext';
import { 
  QA_CREDENTIALS, 
  QA_SCENARIO_CONFIG, 
  getSavedChecklistState, 
  saveChecklistState, 
  clearChecklistState,
  resetStagingScenario,
  ensureStagingTestData,
  QaChecklistState 
} from '../../lib/stagingQaService';
import { getTodayISTDateString } from '../../utils/dateUtils';

interface StagingQaDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ChecklistSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  items: { id: string; label: string; hint?: string }[];
}

const CHECKLIST_SECTIONS: ChecklistSection[] = [
  {
    id: 'auth',
    title: '1. Authentication',
    icon: <Lock className="w-4 h-4 text-amber-500" />,
    items: [
      { id: 'auth_student_login', label: 'Student login (student-test@saremi.academy)', hint: 'Log in with test student credentials' },
      { id: 'auth_teacher_login', label: 'Teacher login (teacher-test@saremi.academy)', hint: 'Log in with test faculty credentials' },
      { id: 'auth_admin_login', label: 'Admin login (admin-test@saremi.academy)', hint: 'Log in with test admin credentials' },
      { id: 'auth_logout', label: 'Logout functionality works cleanly' },
      { id: 'auth_refresh', label: 'Page refresh persists authenticated session & active role' }
    ]
  },
  {
    id: 'enrollment',
    title: '2. Enrollment & Credits',
    icon: <BookOpen className="w-4 h-4 text-emerald-500" />,
    items: [
      { id: 'enr_pkg_visible', label: 'Test package card visible in Student Sanctuary' },
      { id: 'enr_course_correct', label: 'Correct course: Hindustani Classical Vocals' },
      { id: 'enr_pkg_correct', label: 'Correct package: QA 4-Session Foundation Module' },
      { id: 'enr_duration_correct', label: 'Correct duration: 45 minutes per class' },
      { id: 'enr_credits_correct', label: 'Correct total credits: 4 remaining, 0 completed' },
      { id: 'enr_teacher_assigned', label: 'Teacher assigned: Ustad Rashid Khan' },
      { id: 'enr_first_class_unbooked', label: 'Unbooked first-class state: "Schedule First Class" active' }
    ]
  },
  {
    id: 'scheduling',
    title: '3. Scheduling & Concurrency',
    icon: <Calendar className="w-4 h-4 text-blue-500" />,
    items: [
      { id: 'sched_book_first', label: 'Schedule first class via modal' },
      { id: 'sched_teacher_sees', label: 'Teacher sees exact same class on timetable & dashboard' },
      { id: 'sched_student_sees', label: 'Student sees exact same class in Live Classroom tab' },
      { id: 'sched_ist_correct', label: 'Date/time displayed correctly in Asia/Kolkata (IST)' },
      { id: 'sched_no_double_click', label: 'No duplicate booking on double-click (Atomic transaction)' },
      { id: 'sched_student_conflict', label: 'Student conflict detection prevents double booking same hour' },
      { id: 'sched_teacher_conflict', label: 'Teacher conflict detection prevents overlapping faculty slots' },
      { id: 'sched_reschedule', label: 'Reschedule class updates time without mutating credits' },
      { id: 'sched_cancel', label: 'Cancel class restores state without adding extra credits' },
      { id: 'sched_credit_intact', label: 'Credits remain exactly 4 (Booking does NOT deduct credit)' }
    ]
  },
  {
    id: 'cloudflare_studio',
    title: '4. Cloudflare Realtime Studio (Hardware QA)',
    icon: <Video className="w-4 h-4 text-purple-500" />,
    items: [
      { id: 'cf_student_enter', label: 'Student enters Live Studio (/classroom)' },
      { id: 'cf_teacher_enter', label: 'Teacher enters Live Studio (/classroom)' },
      { id: 'cf_same_room', label: 'Both join exact same Cloudflare room' },
      { id: 'cf_student_cam', label: 'Student camera feed streams video' },
      { id: 'cf_teacher_cam', label: 'Teacher camera feed streams video' },
      { id: 'cf_student_mic', label: 'Student microphone streams audio' },
      { id: 'cf_teacher_mic', label: 'Teacher microphone streams audio' },
      { id: 'cf_student_mute', label: 'Student mute/unmute toggles track successfully' },
      { id: 'cf_teacher_mute', label: 'Teacher mute/unmute toggles track successfully' },
      { id: 'cf_cam_toggle', label: 'Camera on/off toggle works smoothly' },
      { id: 'cf_leave_rejoin', label: 'Leave studio and rejoin without token error' },
      { id: 'cf_mobile_safari', label: 'Mobile Safari test: Video & Audio functional' },
      { id: 'cf_mobile_chrome', label: 'Mobile Chrome test: Video & Audio functional' }
    ]
  },
  {
    id: 'postclass',
    title: '5. Post-Class Academic Workflow',
    icon: <FileText className="w-4 h-4 text-amber-500" />,
    items: [
      { id: 'post_eval_modal', label: 'Teacher opens post-class evaluation modal' },
      { id: 'post_attendance', label: 'Attendance marked (Present / Late / Absent)' },
      { id: 'post_notes', label: 'Lesson notes and riyaaz feedback entered' },
      { id: 'post_homework', label: 'Homework / assignment assigned to student' },
      { id: 'post_complete', label: 'Class marked completed via atomic batch write' },
      { id: 'post_credit_deducted', label: 'Exactly ONE credit deducted (4 → 3 remaining, 1 completed)' },
      { id: 'post_idempotency', label: 'Refresh does NOT deduct credit again (creditDeducted lock)' },
      { id: 'post_next_class', label: 'Student sees "Schedule Next Class" with 3 credits remaining' }
    ]
  },
  {
    id: 'multicourse',
    title: '6. Multi-Course Isolation',
    icon: <Layers className="w-4 h-4 text-cyan-500" />,
    items: [
      { id: 'multi_second_course', label: 'Second course enrolled (e.g., Tabla or Guitar)' },
      { id: 'multi_separate_teacher', label: 'Separate teacher assigned for second course' },
      { id: 'multi_separate_credits', label: 'Separate credit pools tracked independently' },
      { id: 'multi_isolated_complete', label: 'Completing Class in Course A does NOT deduct Course B' }
    ]
  },
  {
    id: 'admin',
    title: '7. Admin Operations',
    icon: <Shield className="w-4 h-4 text-rose-500" />,
    items: [
      { id: 'admin_student_mgmt', label: 'Student directory: View student progress & enrollments' },
      { id: 'admin_teacher_mgmt', label: 'Faculty directory: Manage teachers & assignments' },
      { id: 'admin_schedule_mgmt', label: 'Academy Timetable: View all scheduled classes & closures' },
      { id: 'admin_attendance_logs', label: 'Attendance logs & lesson archives viewable' },
      { id: 'admin_payment_status', label: 'Payment transactions & orders verified' },
      { id: 'admin_class_history', label: 'Historical classes and audit trail fully intact' }
    ]
  }
];

export const StagingQaDrawer: React.FC<StagingQaDrawerProps> = ({ isOpen, onClose }) => {
  const { user, profile, role, signInWithEmail, logout } = useAuth();
  const { navigate } = useRouter();

  const [checklist, setChecklist] = useState<QaChecklistState>(() => getSavedChecklistState());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resetFeedback, setResetFeedback] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'checklist' | 'diagnostics' | 'accounts'>('checklist');

  useEffect(() => {
    if (isOpen) {
      // Fetch live diagnostics
      fetch('/api/qa/diagnostics')
        .then((res) => res.json())
        .then((data) => setDiagnostics(data))
        .catch((err) => console.warn('Diagnostics fetch note:', err));
    }
  }, [isOpen]);

  const toggleCheck = (itemId: string) => {
    const next = { ...checklist, [itemId]: !checklist[itemId] };
    setChecklist(next);
    saveChecklistState(next);
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleQuickLogin = async (targetRole: 'student' | 'teacher' | 'admin') => {
    try {
      const creds = QA_CREDENTIALS[targetRole];
      await logout().catch(() => {});
      await signInWithEmail(creds.email, creds.password);
      if (targetRole === 'admin') navigate('/admin');
      else if (targetRole === 'teacher') navigate('/teacher-app');
      else navigate('/app');
      onClose();
    } catch (err: any) {
      console.warn('[QA Drawer] Quick login note:', err?.message);
    }
  };

  const handleResetScenario = async () => {
    setIsResetting(true);
    setResetFeedback(null);
    try {
      const res = await resetStagingScenario();
      setResetFeedback(res.message);
      setTimeout(() => setResetFeedback(null), 5000);
    } catch (err: any) {
      setResetFeedback('Reset failed: ' + err.message);
    } finally {
      setIsResetting(false);
    }
  };

  const handleClearChecklist = () => {
    if (window.confirm('Reset all checklist checkmarks to uncompleted?')) {
      clearChecklistState();
      setChecklist({});
    }
  };

  // Compute total progress
  const totalItems = CHECKLIST_SECTIONS.reduce((acc, sec) => acc + sec.items.length, 0);
  const completedItems = Object.values(checklist).filter(Boolean).length;
  const progressPercent = Math.round((completedItems / totalItems) * 100);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex justify-end bg-black/60 backdrop-blur-sm animate-fade-in">
      <div 
        className="w-full max-w-xl bg-[#14120E] text-[#F3EFEA] h-full flex flex-col shadow-2xl border-l border-amber-500/30 overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
      >
        {/* Drawer Header */}
        <div className="p-4 bg-gradient-to-r from-amber-950 via-[#1A1814] to-amber-950 border-b border-amber-500/30 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500 text-black flex items-center justify-center font-bold shadow">
              <FlaskConical className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-amber-200">Saremi QA Command Center</h2>
                <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/40 text-[10px] font-mono font-bold">
                  STAGING
                </span>
              </div>
              <p className="text-xs text-amber-300/70">Real-world Mobile & Desktop Verification</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Bar & Quick Navigation */}
        <div className="px-4 py-3 bg-[#1B1813] border-b border-amber-500/20">
          <div className="flex items-center justify-between text-xs mb-1.5 font-medium">
            <span className="text-amber-300 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" />
              Checklist Progress: <strong>{completedItems} / {totalItems} items ({progressPercent}%)</strong>
            </span>
            <button
              onClick={handleClearChecklist}
              className="text-[11px] text-amber-400/60 hover:text-amber-300 underline"
            >
              Clear Marks
            </button>
          </div>
          <div className="w-full bg-black/50 h-2 rounded-full overflow-hidden border border-amber-500/20">
            <div
              className="bg-gradient-to-r from-amber-500 to-amber-300 h-full transition-all duration-300 rounded-full"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={() => setActiveTab('checklist')}
              className={`flex-1 py-1.5 px-2 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
                activeTab === 'checklist'
                  ? 'bg-amber-500 text-black shadow'
                  : 'bg-white/5 text-amber-200/80 hover:bg-white/10'
              }`}
            >
              <CheckSquare className="w-3.5 h-3.5" />
              QA Checklist
            </button>
            <button
              onClick={() => setActiveTab('diagnostics')}
              className={`flex-1 py-1.5 px-2 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
                activeTab === 'diagnostics'
                  ? 'bg-amber-500 text-black shadow'
                  : 'bg-white/5 text-amber-200/80 hover:bg-white/10'
              }`}
            >
              <Activity className="w-3.5 h-3.5" />
              Live Diagnostics
            </button>
            <button
              onClick={() => setActiveTab('accounts')}
              className={`flex-1 py-1.5 px-2 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
                activeTab === 'accounts'
                  ? 'bg-amber-500 text-black shadow'
                  : 'bg-white/5 text-amber-200/80 hover:bg-white/10'
              }`}
            >
              <User className="w-3.5 h-3.5" />
              Test Accounts
            </button>
          </div>
        </div>

        {/* Reset Feedback Notification */}
        {resetFeedback && (
          <div className="mx-4 mt-3 p-2.5 bg-amber-500 text-black font-bold text-xs rounded-lg flex items-center justify-between shadow-lg">
            <span>{resetFeedback}</span>
            <button onClick={() => setResetFeedback(null)} className="text-black/80 hover:text-black">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activeTab === 'checklist' && (
            <div className="space-y-4">
              {CHECKLIST_SECTIONS.map((section) => {
                const sectionCompleted = section.items.filter((i) => checklist[i.id]).length;
                const isAllSectionPassed = sectionCompleted === section.items.length;

                return (
                  <div
                    key={section.id}
                    className="p-3.5 rounded-xl bg-[#1A1813] border border-amber-500/20 hover:border-amber-500/40 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-white/5">
                      <div className="flex items-center gap-2">
                        {section.icon}
                        <h3 className="text-xs font-bold text-amber-200 uppercase tracking-wider">
                          {section.title}
                        </h3>
                      </div>
                      <span className={`text-[11px] font-mono px-2 py-0.5 rounded ${
                        isAllSectionPassed 
                          ? 'bg-emerald-500/20 text-emerald-400 font-bold border border-emerald-500/30'
                          : 'bg-white/5 text-amber-300/70'
                      }`}>
                        {sectionCompleted}/{section.items.length} Passed
                      </span>
                    </div>

                    <div className="space-y-2">
                      {section.items.map((item) => {
                        const isChecked = Boolean(checklist[item.id]);
                        return (
                          <div
                            key={item.id}
                            onClick={() => toggleCheck(item.id)}
                            className={`flex items-start gap-2.5 p-2 rounded-lg cursor-pointer transition-all select-none ${
                              isChecked
                                ? 'bg-emerald-950/40 border border-emerald-500/30 text-emerald-200'
                                : 'bg-black/30 hover:bg-white/5 text-neutral-300 border border-transparent'
                            }`}
                          >
                            <div className="mt-0.5 flex-shrink-0">
                              {isChecked ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                              ) : (
                                <Circle className="w-4 h-4 text-neutral-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-medium leading-snug ${isChecked ? 'line-through text-emerald-300/80' : 'text-neutral-200'}`}>
                                {item.label}
                              </p>
                              {item.hint && (
                                <p className="text-[10px] text-neutral-400 mt-0.5">{item.hint}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'diagnostics' && (
            <div className="space-y-4">
              <div className="p-3.5 rounded-xl bg-[#1A1813] border border-amber-500/20 space-y-3">
                <div className="flex items-center justify-between pb-2 border-b border-white/5">
                  <h3 className="text-xs font-bold text-amber-200 uppercase tracking-wider flex items-center gap-1.5">
                    <Activity className="w-4 h-4 text-amber-400" />
                    Environment Status
                  </h3>
                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-bold rounded">
                    OPERATIONAL
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2.5 bg-black/40 rounded-lg border border-white/5">
                    <span className="text-neutral-400 text-[10px] block">Target Environment</span>
                    <strong className="text-amber-400 font-mono">STAGING (Safe Sandbox)</strong>
                  </div>
                  <div className="p-2.5 bg-black/40 rounded-lg border border-white/5">
                    <span className="text-neutral-400 text-[10px] block">Razorpay Mode</span>
                    <strong className="text-emerald-400 font-mono">TEST / SANDBOX</strong>
                  </div>
                  <div className="p-2.5 bg-black/40 rounded-lg border border-white/5">
                    <span className="text-neutral-400 text-[10px] block">Timezone</span>
                    <strong className="text-neutral-200 font-mono">Asia/Kolkata (IST)</strong>
                  </div>
                  <div className="p-2.5 bg-black/40 rounded-lg border border-white/5">
                    <span className="text-neutral-400 text-[10px] block">Cloudflare Token Route</span>
                    <strong className="text-purple-300 font-mono">/api/cloudflare/room-token</strong>
                  </div>
                </div>

                <div className="p-2.5 bg-black/40 rounded-lg border border-white/5 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Current Authenticated UID:</span>
                    <span className="text-amber-300 font-mono">{user?.uid || 'Not Authenticated'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Current Role:</span>
                    <span className="text-emerald-400 font-bold uppercase">{role}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Target Test Enrollment:</span>
                    <span className="text-cyan-300 font-mono">qa_enrollment_saremi_2026</span>
                  </div>
                </div>
              </div>

              {/* Reset Test Scenario Action Card */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-amber-950/60 to-black border border-amber-500/40 space-y-2.5">
                <div className="flex items-center gap-2 text-amber-200 font-bold text-xs">
                  <RotateCcw className="w-4 h-4 text-amber-400" />
                  Reset QA Test Scenario
                </div>
                <p className="text-[11px] text-amber-200/70 leading-relaxed">
                  Safely resets the test student's enrollment back to pristine state: <strong>4 credits remaining</strong>, <strong>0 classes completed</strong>, and cleans up any scheduled test classes. Zero impact on production data.
                </p>
                <button
                  onClick={handleResetScenario}
                  disabled={isResetting}
                  className="w-full py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg text-xs flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 disabled:opacity-50"
                >
                  <RotateCcw className={`w-3.5 h-3.5 ${isResetting ? 'animate-spin' : ''}`} />
                  {isResetting ? 'Resetting Scenario...' : 'Execute Test Scenario Reset'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'accounts' && (
            <div className="space-y-3">
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-300 flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>
                  Tap <strong>"1-Click Sign In"</strong> to instantly authenticate on mobile or copy credentials to sign in manually.
                </span>
              </div>

              {/* Student Card */}
              <div className="p-3.5 rounded-xl bg-[#1A1813] border border-amber-500/30 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                      🎓
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-white">{QA_CREDENTIALS.student.name}</h4>
                      <span className="text-[10px] text-blue-400 font-semibold">STUDENT (4 Credits Initial)</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleQuickLogin('student')}
                    className="px-2.5 py-1 bg-blue-600 hover:bg-blue-500 text-white font-bold text-[11px] rounded shadow"
                  >
                    1-Click Sign In
                  </button>
                </div>

                <div className="bg-black/50 p-2 rounded border border-white/5 text-[11px] font-mono space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">Email:</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-white">{QA_CREDENTIALS.student.email}</span>
                      <button onClick={() => handleCopy(QA_CREDENTIALS.student.email, 'std_em')}>
                        {copiedKey === 'std_em' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-neutral-400" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">Password:</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-amber-300">{QA_CREDENTIALS.student.password}</span>
                      <button onClick={() => handleCopy(QA_CREDENTIALS.student.password, 'std_pw')}>
                        {copiedKey === 'std_pw' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-neutral-400" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Teacher Card */}
              <div className="p-3.5 rounded-xl bg-[#1A1813] border border-amber-500/30 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-amber-500 text-black flex items-center justify-center text-xs font-bold">
                      👨‍🏫
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-white">{QA_CREDENTIALS.teacher.name}</h4>
                      <span className="text-[10px] text-amber-400 font-semibold">FACULTY GURU (Hindustani Classical)</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleQuickLogin('teacher')}
                    className="px-2.5 py-1 bg-amber-500 hover:bg-amber-400 text-black font-bold text-[11px] rounded shadow"
                  >
                    1-Click Sign In
                  </button>
                </div>

                <div className="bg-black/50 p-2 rounded border border-white/5 text-[11px] font-mono space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">Email:</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-white">{QA_CREDENTIALS.teacher.email}</span>
                      <button onClick={() => handleCopy(QA_CREDENTIALS.teacher.email, 'tch_em')}>
                        {copiedKey === 'tch_em' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-neutral-400" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">Password:</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-amber-300">{QA_CREDENTIALS.teacher.password}</span>
                      <button onClick={() => handleCopy(QA_CREDENTIALS.teacher.password, 'tch_pw')}>
                        {copiedKey === 'tch_pw' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-neutral-400" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Admin Card */}
              <div className="p-3.5 rounded-xl bg-[#1A1813] border border-amber-500/30 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center text-xs font-bold">
                      🛡️
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-white">{QA_CREDENTIALS.admin.name}</h4>
                      <span className="text-[10px] text-rose-400 font-semibold">ACADEMY ADMINISTRATOR</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleQuickLogin('admin')}
                    className="px-2.5 py-1 bg-rose-600 hover:bg-rose-500 text-white font-bold text-[11px] rounded shadow"
                  >
                    1-Click Sign In
                  </button>
                </div>

                <div className="bg-black/50 p-2 rounded border border-white/5 text-[11px] font-mono space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">Email:</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-white">{QA_CREDENTIALS.admin.email}</span>
                      <button onClick={() => handleCopy(QA_CREDENTIALS.admin.email, 'adm_em')}>
                        {copiedKey === 'adm_em' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-neutral-400" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-400">Password:</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-amber-300">{QA_CREDENTIALS.admin.password}</span>
                      <button onClick={() => handleCopy(QA_CREDENTIALS.admin.password, 'adm_pw')}>
                        {copiedKey === 'adm_pw' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-neutral-400" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="p-3 bg-[#1B1813] border-t border-amber-500/30 flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5 text-amber-300/80">
            <Smartphone className="w-4 h-4 text-amber-400" />
            <span className="hidden sm:inline">Mobile & Desktop Responsive Viewport Ready</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg"
          >
            Close Panel
          </button>
        </div>
      </div>
    </div>
  );
};
